import { ComponentType, ReactNode, useCallback, useEffect, useState } from 'react'
import { Loading } from 'notiflix'
import { Report } from 'notiflix/build/notiflix-report-aio'
import { useNavigate } from 'react-router-dom'
import Sidebar from '../components/layout/sidebar/sidebar'
import { MainView } from '../components/layout/main'
import api from '../helpers/apiRequester'
import { getLoginUrl } from '../helpers/authConfig'
import { Page, PageHeader } from '../ui'

/**
 * Shared shell for the Comms Management pages: probes one endpoint to check
 * access, then renders the view. Keeps the auth/redirect behaviour identical
 * across Inbox, Mailboxes, Categories and Models.
 */
export const makeCommsPage = ({ title, intro, probeUrl, probeData, View, className, viewRendersHeader = false }: { title: string; intro?: ReactNode; probeUrl: string; probeData?: Record<string, unknown>; View: ComponentType; className: string; /** The view draws its own PageHeader (e.g. a per-record workspace whose title depends on the route). */ viewRendersHeader?: boolean }) => {
    const CommsPage = () => {
        const navigate = useNavigate()
        const [dataReady, setDataReady] = useState(false)

        const checkAuth = useCallback(async () => {
            Loading.circle('Loading...')
            try {
                await api({ url: probeUrl, data: probeData ?? {} })
                setDataReady(true)
                Loading.remove()
            } catch (error: any) {
                Loading.remove()
                if (error?.response?.status === 401) {
                    Report.failure('Session Timed Out', 'Please log in again to continue.', 'Okay', () => {
                        window.location.href = getLoginUrl()
                    })
                } else if (error?.response?.status === 403) {
                    Report.failure('Access Denied', `The ${title} page is restricted to Sentinel users only.`, 'Return to Dashboard', () => navigate('/home'))
                } else {
                    Report.failure('Access Denied', 'You do not have permission to view this page.', 'Okay', () => navigate('/home'))
                }
            }
        }, [navigate])

        useEffect(() => {
            checkAuth()
        }, [checkAuth])

        return (
            <div className={className}>
                <Sidebar />
                <MainView>
                    <Page>
                        {!viewRendersHeader && <PageHeader eyebrow="Comms Management" title={title} intro={intro} />}
                        {dataReady && <View />}
                    </Page>
                </MainView>
            </div>
        )
    }
    CommsPage.displayName = `${className}Page`
    return CommsPage
}
